/** @format */

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { KillswitchService, ProductAnalyticsService } from '@shared/data-access/services';
import { Observable, combineLatest, merge, of, switchMap, zip } from 'rxjs';
import {
	addProductToSet,
	removeProductFromSet,
	selectAllProductSkus,
	selectAllProductStyle,
	selectBundleInStorePickupButtonText,
	selectBundleShipToMeButtonText,
	selectIsBOPISLOnly,
	selectNearbyStores,
	setBopisInventory,
	setEcommInventory,
	setNearbyStores,
	setResolvedSku,
	setSDDInfo
} from '@pdp/data-access/state';
import { map, take } from 'rxjs/operators';
import {
	selectInStorePickupButtonText,
	selectSameDayDeliveryButtonText,
	selectShipMode,
	selectShipToMeButtonText
} from './fulfillment-buttons.selector';

import { Chain } from '@shared/data-access/interfaces';
import { FulfillmentButtonInterface } from '../../interfaces/fulfillment-button.interface';
import { Injectable } from '@angular/core';
import { ProductSelectionService } from '@pdp/data-access/services';
import { Store } from '@ngrx/store';
import { UtilityService } from '@shared/utils/services';
import { concatLatestFrom } from '@ngrx/operators';
import { noopAction } from '@state/data-access';
import { setATCFulfillmentOption } from '../add-to-cart.actions';
import { setFulfillmentButtonsShipMode } from './fulfillment-buttons.actions';

@Injectable()
export class FulfillmentButtonsEffects {
	nearbyStoresButtonEnabled: boolean;
	isExceedsInventoryMsgEnabled: boolean;

	constructor(
		private readonly actions$: Actions,
		private readonly store: Store,
		private readonly productSelectionService: ProductSelectionService,
		private readonly killswitchService: KillswitchService,
		private readonly productAnalyticsService: ProductAnalyticsService,
		private readonly utilityService: UtilityService,
		private readonly chain: Chain
	) {
		this.killswitchService.getKillSwitchesEnvironment$().subscribe((data) => {
			this.nearbyStoresButtonEnabled = this.utilityService.checkKillSwitch(
				'nearbyStoresButtonEnabled',
				data,
				this.chain.chainIdentifierAbbr
			);
			this.isExceedsInventoryMsgEnabled = this.utilityService.checkKillSwitch(
				'isExceedsInventoryMsgEnabled',
				data,
				this.chain.chainIdentifierAbbr
			);
		});
	}

	updateAtcFulfillmentOption$ = createEffect(() =>
		this.actions$.pipe(
			ofType(setFulfillmentButtonsShipMode),
			map((action) => setATCFulfillmentOption({ atcFulfillmentOption: action.shipMode }))
		)
	);

	maybeAutoSelectFulfillmentOption$ = createEffect(() =>
		merge(
			// Wait for inventory to be populated on non-bundle items...
			zip([
				this.actions$.pipe(ofType(setEcommInventory)),
				this.actions$.pipe(ofType(setBopisInventory)),
				this.actions$.pipe(ofType(setSDDInfo))
			]),
			// ...or fire whenever SKU changes (e.g. attribute changed)
			this.actions$.pipe(ofType(setResolvedSku)),
			// ...or fire whenever set is changed on bundle items
			this.actions$.pipe(ofType(addProductToSet, removeProductFromSet))
		).pipe(
			concatLatestFrom(() => [
				this.store.select(selectAllProductStyle),
				this.store.select(selectNearbyStores),
				this.store.select(selectAllProductSkus),
				this.store.select(selectIsBOPISLOnly)
			]),
			switchMap(([, productData, nearbyStoresData, productSkus, isBOPISLOnly]) => {
				// only auto-switch if nearbyStoresButtonEnabled killswitch is enabled
				// also, don't switch if is BOPISL only
				if (!this.nearbyStoresButtonEnabled || isBOPISLOnly) {
					return of(noopAction());
				}

				const isBundle = this.checkIfBundle(productData);

				// if out of stock, do nothing
				if (!isBundle && (!productSkus || productSkus.length === 0)) {
					return of(noopAction());
				}

				// observable data type does not matter, so use `unknown` here.
				// this is necessary -- see https://github.com/ReactiveX/rxjs/issues/3388#issuecomment-373480367
				const maybeWaitForNearbyStoresData: Observable<unknown> =
					// if we don't have store data yet but we are going to get store data, wait for store data to come in
					// otherwise, just fire immediately
					nearbyStoresData === null && window.headerFunctions?.getStoreInfo
						? this.actions$.pipe(ofType(setNearbyStores))
						: of(true);

				return maybeWaitForNearbyStoresData.pipe(
					switchMap(() => {
						const selectors = this.getSelectors(isBundle);
						// Get fulfillment option availability state
						return combineLatest(selectors).pipe(
							take(1),
							concatLatestFrom(() => this.store.select(selectShipMode)),
							map(
								([
									[shipToMeButtonText, inStorePickupButtonText, sddButtonText],
									shipMode
								]) => {
									// Don't handle fulfillment options if they don't exist at all (e.g. on custom products)
									if (!shipMode) {
										return noopAction();
									}
									return this.handleFulfillmentOptions(
										shipToMeButtonText,
										inStorePickupButtonText,
										sddButtonText,
										shipMode
									);
								}
							)
						);
					})
				);
			})
		)
	);

	private checkIfBundle(productData) {
		if (Object.keys(productData).length === 0) {
			const bundleData = this.productSelectionService.getBundleData();
			if (Object.keys(bundleData).length > 1) {
				return true;
			}
		}
		return false;
	}

	private getSelectors(isBundle: boolean) {
		return isBundle
			? [
					this.store.select(selectBundleShipToMeButtonText),
					this.store.select(selectBundleInStorePickupButtonText)
				]
			: [
					this.store.select(selectShipToMeButtonText),
					this.store.select(selectInStorePickupButtonText),
					this.store.select(selectSameDayDeliveryButtonText)
				];
	}

	private handleFulfillmentOptions(
		shipToMeButtonText: FulfillmentButtonInterface,
		inStorePickupButtonText: FulfillmentButtonInterface,
		sddButtonText: FulfillmentButtonInterface,
		shipMode: string
	) {
		// compile availability info
		const shipModesAvailable = [
			{
				shipMode: 'shipToMe',
				available: !shipToMeButtonText.fulfillmentButtonDisabledClass,
				lowerQtyAvailable: shipToMeButtonText.isLowerQuantityAvailable
			},
			{
				shipMode: inStorePickupButtonText.isBopl ? 'bopl' : 'bopis',
				available: !inStorePickupButtonText.fulfillmentButtonDisabledClass,
				lowerQtyAvailable: inStorePickupButtonText.isLowerQuantityAvailable
			},
			{
				shipMode: 'sameDayDelivery',
				available: sddButtonText && !sddButtonText.fulfillmentButtonDisabledClass,
				lowerQtyAvailable: sddButtonText && inStorePickupButtonText.isLowerQuantityAvailable
			}
		];

		const currentShipMode = shipModesAvailable.find((mode) => mode.shipMode === shipMode);

		// check if currently-selected ship mode is available
		// if it is, don't change it
		if (currentShipMode?.available) {
			return noopAction();
		}

		// if exceeded inventory scenario is enabled, current shipmode is not available, and inventory is nonzero, don't switch ship mode
		if (this.isExceedsInventoryMsgEnabled && currentShipMode?.lowerQtyAvailable) {
			return noopAction();
		}

		// if STH is available, we always choose STH; if not, we always choose pickup
		// therefore, the order of precedence is STH, pickup, SDD
		const shipModeToSelect = shipModesAvailable.find((mode) => mode.available);

		if (!shipModeToSelect) {
			// if none are available, don't switch
			return noopAction();
		}

		// Fire analytics when switching from between STH and BOPIS/BOPL, ignore SDD
		if (shipMode !== 'sameDayDelivery' && shipModeToSelect.shipMode !== 'sameDayDelivery') {
			this.productAnalyticsService.triggerAnalyticsEvent('multiUseAnalyticsEvent', {
				event: 'opt1954',
				value: 'bopisAutoSwap'
			});
		}

		return setFulfillmentButtonsShipMode({
			shipMode: shipModeToSelect.shipMode
		});
	}
}
